import React, { useCallback, useImperativeHandle, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import _ from 'lodash'
import { ConfigProvider } from 'antd-v5';
import { GlobalSettingsContextProvider } from 'contexts/GlobalSettingsContext';
import { ActiveUserContextProvider } from 'contexts/ActiveUserContext';
import GlobalUserListContextProvider from 'contexts/GlobalUserListContext';
import ConfigurationProvider from 'components/ConfigurationProvider';
import { getAntdV5Theme } from 'constants/AntdV5Theme';

const references = new Map();
function showPortal({content, afterClose = _.noop}) {
    const element = document.createElement('div');
    const target = document.getElementById('root-modal-wrapper') || document.body;
    target.append(element);

    function _afterClose(...args){
        afterClose(...args);
        ReactDOM.unmountComponentAtNode(element);
        element.remove();
        references.delete(element);
    }

    const _content = React.cloneElement(content, {
        afterClose: _afterClose,
        ref: (ref) => ref && references.set(element, ref)
    });
    
    const isHighContrastMode = document.documentElement.getAttribute('data-high-contrast-mode') === 'true';

    ReactDOM.render(
        <ConfigProvider prefixCls="ant-v5" theme={getAntdV5Theme(isHighContrastMode)}>
            <ConfigurationProvider>
                <GlobalUserListContextProvider>
                    <ActiveUserContextProvider>
                        <GlobalSettingsContextProvider>
                            {_content}
                        </GlobalSettingsContextProvider>
                    </ActiveUserContextProvider>
                </GlobalUserListContextProvider>
            </ConfigurationProvider>
        </ConfigProvider>
    , element);

    return () => references.get(element)?.close();
}

function closeAll(){
    references.forEach((ref) => {
        if(typeof ref?.close === 'function'){
            ref.close()
        }
    })
    references.clear();
}

function useModal(){
    const ref = useRef();
    const showModal = useCallback((content) => {
        ref.current.addModal(content);
    }, []);

    const component = <ModalRenderer ref={ref} />
    return [showModal, component];
}

let modalIdCounter = 0;

// ENG-111079: ModalRenderer previously never removed modals from its array,
// causing closed modals (and their entire component trees) to stay mounted forever.
// For example, the widget config popup's live preview kept a ghost FindingStatsProvider
// mounted, resulting in duplicate API calls when dashboard filters changed.
// Fix: inject an afterClose callback via cloneElement that removes the modal from
// state after Ant Design's Modal close animation finishes, mirroring the cleanup
// pattern already used by showPortal above.
const ModalRenderer = React.forwardRef(function ModalRenderer(props, ref){
    const [modals, setModals] = useState([]);

    useImperativeHandle(ref, () => ({
        addModal: (content) => {
            const id = ++modalIdCounter;
            const originalAfterClose = content.props.afterClose;
            const wrappedContent = React.cloneElement(content, {
                afterClose: (...args) => {
                    originalAfterClose?.(...args);
                    setModals((prev) => prev.filter(m => m.id !== id));
                }
            });
            setModals((prev) => [...prev, { id, content: wrappedContent }]);
        }
    }), []);

    return modals.map((m) => (
      <React.Fragment key={`modal-${m.id}`}>{m.content}</React.Fragment>
    ));
})

const ModalUtil = {
    show: showPortal,
    useModal,
    closeAll
}

export default ModalUtil;
